import usePostApi from '@/composables/post/postApi'
import { Post } from '@/model/data-object/Post'
import usePostFactory from '@/composables/post/postFactory'
import { Ref, ref } from 'vue'
import { PostUpdate } from '@/model/data-object/PostUpdate'
import usePostList from '@/composables/post/postList'
import validatePost from '@/validation/postValidator'
import ValidationError from '@/error/validationError'
import ApiError from '@/error/apiError'
import ApiValidationError from '@/error/apiValidationError'
import AppValidationError from '@/error/appValidationError'
import useAnalyticsManager, {
  ANALYTICS_EVENT_POST_EDIT_SAVE,
} from '@/composables/analytics/analyticsManager'

export default async function usePostSingleLoader(post: Post) {
  const { fetchSingleWithRawContent } = usePostApi()
  const { createFromObject } = usePostFactory()

  const { request, response } = fetchSingleWithRawContent(post.id)
  await request()

  if (response.value) {
    return ref<Post>(createFromObject(response.value as Post))
  }

  return null
}

export function usePostUpdate(post: Post, emit: any) {
  const { update } = usePostApi()
  const { propagateAnalyticsEvent } = useAnalyticsManager()
  const { updateFromObject } = usePostFactory()
  const { findPostRoot } = usePostList()

  const validationError: Ref<ValidationError | undefined> = ref()
  const apiError: Ref<ApiError | undefined> = ref()

  const postUpdate = new PostUpdate()
  postUpdate.id = post.id
  postUpdate.content = post.contentRaw
  postUpdate.topicId = post.topic.id

  const { request, loading, error, response } = update(postUpdate)

  const handleError = (error: Error) => {
    if (
      error instanceof ApiValidationError ||
      error instanceof AppValidationError
    ) {
      validationError.value = error
    } else if (error instanceof ApiError) {
      apiError.value = error
    }
  }

  const updatePost = async () => {
    postUpdate.content = post.contentRaw
    try {
      validatePost(postUpdate)
      propagateAnalyticsEvent(post, ANALYTICS_EVENT_POST_EDIT_SAVE)
      await request()

      if (error.value) {
        handleError(error.value)

        return
      }

      if (response.value) {
        const postRoot = findPostRoot(post.id)
        if (postRoot && postRoot.post) {
          updateFromObject(postRoot.post, response.value as Post)
          postRoot.post.modified = true
        }
        emit('post-updated', response.value)
      }
    } catch (err) {
      handleError(err)
    }
  }

  return {
    loading,
    updatePost,
    validationError,
    apiError,
  }
}
